// WindowElevationsSection.jsx
// Phase 2: Window Elevations Section - Extracted from ProjectDetail.js

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Trash2, ArrowLeft, ZoomIn, ZoomOut, RotateCcw,
  Eye, X, Loader2, ClipboardPaste 
} from 'lucide-react';
import { isZoomAtDefault } from '@/utils/zoom';
import { ElevationLoadingIndicator } from '@/features/shared/ElevationViewer';
import { MarkerActionsPopup } from '../shared/MarkerActionsPopup';

export const WindowElevationsSection = ({
  // Data
  windowElevations,
  windowList,
  windowTypes,
  selectedWindowElevation,
  selectedWindowElevationIds,
  canEditCSIItems,
  // Loading states
  uploadingElevation,
  bulkDeletingElevations,
  deletingSingleElevation,
  elevationUploading,
  uploadProgress,
  uploadProgressPercent,
  // Zoom/Pan state
  elevationZoom,
  elevationPan,
  isPanning,
  // Marker state
  bulkMarkerMode,
  bulkMarkerSchedule,
  pendingBulkMarkers,
  savingBulkMarkers,
  activeMarkerPopup,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onElevationClick,
  onDeleteElevation,
  onBack,
  onPaste,
  onUpload,
  // Zoom handlers
  onZoomIn,
  onZoomOut,
  onResetZoom,
  // Pan handlers
  onPanStart,
  onPanMove,
  onPanEnd,
  // Marker handlers
  onImageClick,
  onMarkerClick,
  onMarkerDelete,
  onMarkerMouseDown,
  onMarkerMouseMove,
  onMarkerMouseUp,
  onSetActiveMarkerPopup,
  // Bulk marker handlers
  onBulkMarkerModeToggle,
  onBulkMarkerScheduleChange,
  onBulkMarkerClick,
  onSaveBulkMarkers,
  onCancelBulkMarkers,
  onRemovePendingMarker,
  // Utilities
  getElevationImageUrl,
  navigate,
  projectId,
  activeCsiTab,
  activeSubTab,
  dragJustEndedRef,
}) => {
  // Helper to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <>
      {/* Loading overlay */}
      {(uploadingElevation || bulkDeletingElevations || deletingSingleElevation) && (
        <ElevationLoadingIndicator 
          message={uploadProgress || 'Processing...'}
          progressPercent={uploadProgressPercent}
        />
      )}
      
      {/* Header - only when not viewing detail */}
      {!selectedWindowElevation && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Elevations</h2>
          <div className="flex gap-2">
            {canEditCSIItems && windowElevations.length > 0 && (
              <>
                <Button 
                  variant="outline"
                  onClick={onSelectAll}
                  disabled={uploadingElevation || bulkDeletingElevations}
                >
                  {selectedWindowElevationIds.length === windowElevations.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button 
                  onClick={onConfirmBulkDelete}
                  disabled={selectedWindowElevationIds.length === 0 || uploadingElevation || bulkDeletingElevations}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedWindowElevationIds.length})
                </Button>
              </>
            )}
            {canEditCSIItems && (
              <>
                <Button 
                  variant="outline"
                  onClick={onPaste}
                  disabled={uploadingElevation || bulkDeletingElevations || elevationUploading === 'window'}
                >
                  {elevationUploading === 'window' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ClipboardPaste className="h-4 w-4 mr-2" />
                  )}
                  Paste
                </Button>
                <Button 
                  onClick={() => document.getElementById('elevation-upload-window').click()}
                  disabled={uploadingElevation || bulkDeletingElevations}
                >
                  {uploadingElevation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {uploadingElevation ? 'Processing...' : 'Upload Drawing'}
                </Button>
                <input
                  id="elevation-upload-window"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={onUpload}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>
        </div>
      )}

      {selectedWindowElevation && selectedWindowElevation.id ? (
        // Elevation Detail View
        <div>
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="outline" 
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Elevations
            </Button>
            {canEditCSIItems && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={(e) => onDeleteElevation(selectedWindowElevation, e)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Elevation
              </Button>
            )}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>{selectedWindowElevation.elevationName || selectedWindowElevation.filename}</CardTitle>
              <CardDescription>
                {bulkMarkerMode 
                  ? 'Click on the image to add multiple markers. Press Done when finished.'
                  : 'Click on the image to mark window locations'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {getElevationImageUrl(selectedWindowElevation) ? (
                <>
                  {/* Bulk Marker Controls + Zoom Controls */}
                  <div className="flex items-center gap-2 mb-3 justify-between">
                    {/* Left side - Bulk Marker Controls */}
                    <div className="flex items-center gap-2">
                      {!bulkMarkerMode ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onBulkMarkerModeToggle(true)}
                          disabled={windowList.length === 0}
                          title="Add Multiple Markers"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Multiple Schedule Markers
                        </Button>
                      ) : (
                        <>
                          <Select
                            value={bulkMarkerSchedule}
                            onValueChange={onBulkMarkerScheduleChange}
                            disabled={savingBulkMarkers}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Select Schedule..." />
                            </SelectTrigger>
                            <SelectContent>
                              {windowList.map((window) => {
                                const windowType = windowTypes.find(wt => wt.id === window.windowTypeId);
                                const typeIndex = windowTypes.findIndex(wt => wt.id === window.windowTypeId);
                                return (
                                  <SelectItem key={window.id} value={window.id}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                                        style={{ backgroundColor: windowType?.markerColor || '#39FF14' }}
                                      />
                                      {window.label} (Type {typeIndex + 1})
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={onSaveBulkMarkers}
                            disabled={pendingBulkMarkers.length === 0 || savingBulkMarkers}
                          >
                            {savingBulkMarkers ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>Done ({pendingBulkMarkers.length})</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={onCancelBulkMarkers}
                            disabled={savingBulkMarkers}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                    
                    {/* Right side - Zoom Controls */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onZoomOut}
                        disabled={elevationZoom <= 0.5}
                        title="Zoom Out"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium text-slate-600 min-w-[60px] text-center">
                        {Math.round(elevationZoom * 100)}%
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onZoomIn}
                        disabled={elevationZoom >= 3}
                        title="Zoom In"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onResetZoom}
                        disabled={isZoomAtDefault(elevationZoom)}
                        title="Reset zoom to 100%"
                        aria-label="Reset zoom to 100%"
                        data-testid="window-elevation-reset-zoom-btn"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Image Container with Pan */}
                  <div 
                    className="relative overflow-auto border border-slate-200 rounded"
                    style={{ 
                      maxHeight: '70vh',
                      cursor: bulkMarkerMode && bulkMarkerSchedule 
                        ? 'crosshair' 
                        : elevationZoom > 1 
                          ? (isPanning ? 'grabbing' : 'grab') 
                          : 'default'
                    }}
                  >
                    {/* Image with Markers */}
                    <div 
                      className="relative inline-block"
                      style={{
                        transform: `scale(${elevationZoom}) translate(${elevationPan.x}px, ${elevationPan.y}px)`,
                        transformOrigin: 'top left',
                        transition: isPanning ? 'none' : 'transform 0.2s ease-out'
                      }}
                      onMouseMove={(e) => {
                        onPanMove(e);
                        onMarkerMouseMove(e);
                      }}
                      onMouseDown={onPanStart}
                      onMouseUp={(e) => {
                        onPanEnd();
                        onMarkerMouseUp(e);
                      }}
                      onMouseLeave={(e) => {
                        onPanEnd();
                        onMarkerMouseUp(e);
                      }}
                    >
                      <img
                        src={getElevationImageUrl(selectedWindowElevation, 'large')}
                        alt={selectedWindowElevation.elevationName || "Elevation"}
                        className="w-full"
                        style={{ cursor: bulkMarkerMode && bulkMarkerSchedule ? 'crosshair' : 'crosshair' }}
                        onClick={canEditCSIItems ? (e) => {
                          if (!isPanning) {
                            if (bulkMarkerMode && bulkMarkerSchedule) {
                              onBulkMarkerClick(e);
                            } else if (!bulkMarkerMode) {
                              onImageClick(e);
                            }
                          }
                        } : null}
                        draggable={false}
                      />
                    
                      {/* Display Existing Markers */}
                      {selectedWindowElevation?.markers && Array.isArray(selectedWindowElevation.markers) && selectedWindowElevation.markers
                        .map((marker) => {
                          const windowItem = windowList.find(w => w.id === marker.windowListId);
                          const windowType = windowItem ? windowTypes.find(wt => wt.id === windowItem.windowTypeId) : null;
                          const markerColor = windowType?.markerColor || '#39FF14';
                          
                          return (
                            <div
                              key={marker.id}
                              className="absolute z-10"
                              style={{
                                left: `${marker.x}%`,
                                top: `${marker.y}%`,
                                transform: 'translate(-50%, -50%)',
                                cursor: canEditCSIItems ? 'move' : 'pointer',
                                padding: '8px',
                                zIndex: activeMarkerPopup === marker.id ? 50 : 10
                              }}
                              onMouseDown={(e) => {
                                if (canEditCSIItems && !e.target.closest('.marker-popup')) {
                                  onMarkerMouseDown(e, marker);
                                }
                              }}
                            >
                              {/* Visible marker box */}
                              <div 
                                className="marker-box"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  border: `2px solid ${markerColor}`,
                                  backgroundColor: activeMarkerPopup === marker.id ? markerColor : hexToRgba(markerColor, 0.1),
                                  boxShadow: `0 0 8px ${hexToRgba(markerColor, 0.6)}`,
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = markerColor}
                                onMouseLeave={(e) => {
                                  if (activeMarkerPopup !== marker.id) {
                                    e.currentTarget.style.backgroundColor = hexToRgba(markerColor, 0.1);
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (dragJustEndedRef?.current) return;
                                  onSetActiveMarkerPopup(activeMarkerPopup === marker.id ? null : marker.id);
                                }}
                              />
                              {/* Popup Controls */}
                              {activeMarkerPopup === marker.id && (
                                <MarkerActionsPopup zoom={elevationZoom}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSetActiveMarkerPopup(null);
                                      onMarkerClick(e, marker);
                                    }}
                                    className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors flex items-center justify-center"
                                    title="View Details"
                                    style={{ minWidth: '24px', minHeight: '24px' }}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                  
                                  {canEditCSIItems && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSetActiveMarkerPopup(null);
                                        onMarkerDelete(marker.id);
                                      }}
                                      className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors flex items-center justify-center"
                                      title="Delete Marker"
                                      style={{ minWidth: '24px', minHeight: '24px' }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSetActiveMarkerPopup(null);
                                    }}
                                    className="p-1 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors flex items-center justify-center"
                                    title="Close"
                                    style={{ minWidth: '24px', minHeight: '24px' }}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </MarkerActionsPopup>
                              )}
                            </div>
                          );
                        })}
                        
                      {/* Display Pending Bulk Markers */}
                      {pendingBulkMarkers.map((marker) => (
                        <div
                          key={marker.id}
                          className="absolute group z-20"
                          style={{
                            left: `${marker.x}%`,
                            top: `${marker.y}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                        >
                          <div 
                            style={{
                              width: '10px',
                              height: '10px',
                              border: `2px dashed ${marker.color}`,
                              backgroundColor: `${marker.color}33`,
                              boxShadow: `0 0 8px ${marker.color}99`
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemovePendingMarker(marker.id);
                            }}
                            className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center"
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-slate-500">No images available. PDF conversion may have failed.</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        // Elevation List View
        <>
          {windowElevations.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-slate-600">No elevations uploaded yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {windowElevations.map((elevation) => (
                <Card 
                  key={elevation.id} 
                  className={`cursor-pointer hover:shadow-lg transition-shadow relative ${selectedWindowElevationIds.includes(elevation.id) ? 'ring-2 ring-blue-500' : ''}`} 
                  onClick={() => onElevationClick(elevation)}
                >
                  {canEditCSIItems && (
                    <>
                      <div 
                        className="absolute top-2 left-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedWindowElevationIds.includes(elevation.id)}
                          onCheckedChange={(checked) => onToggleSelection(elevation.id, { target: { checked } })}
                          className="h-5 w-5"
                        />
                      </div>
                      <button
                        onClick={(e) => onDeleteElevation(elevation, e)}
                        className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <CardContent className="p-4">
                    <div className="aspect-video bg-slate-100 rounded mb-3 flex items-center justify-center overflow-hidden">
                      {getElevationImageUrl(elevation) ? (
                        <img 
                          src={getElevationImageUrl(elevation, 'thumbnail')} 
                          alt="Elevation preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <p className="text-sm text-slate-500">PDF Preview</p>
                      )}
                    </div>
                    <p className="font-medium text-sm truncate">{elevation.elevationName || elevation.filename}</p>
                    {elevation.aiDetected && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">AI Detected</span>
                    )}
                    <p className="text-xs text-slate-500 mt-1">{new Date(elevation.uploadedAt).toLocaleDateString()}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      {elevation.markers?.length || 0} schedule(s) marked
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default WindowElevationsSection;
